<template>
  <v-app class="app-ver-1" :class="{ 'theme--dark': $vuetify.theme.dark }">
    <v-main :class="{ 'block-ui': blockedProject }">
      <transition name="fade-theme" mode="out-in">
        <router-view :key="$route.fullPath"></router-view>
      </transition>

      <error-notification v-if="showErrorNotif" />
      <success-notification />
      <settings/>

      <EmployeeDetails
        v-if="employeeDetails.show && employeeDetails.employee"
        v-model="employeeDetails.show"
        :employee="employeeDetails.employee"
        :notFromEmployeeModule="true"
        @closeDetails="onCloseEmployeeDetails"
      />

      <ProjectDetail
        v-if="projectDetails.show && projectDetails.project"
        v-model="projectDetails"
        :notFromProjectsModule="true"
      />

      <ClientDetail
        v-if="clientDetails.show && clientDetails.client"
        v-model="clientDetails.show"
        :client="clientDetails.client"
        :notFromClientsModule="true"
      />

      <v-overlay :value="overlay">
        <div class="text--white text-h5">Loading...</div>
      </v-overlay>
    </v-main>
  </v-app>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import {
  readStoredUser,
  refreshTokens,
  clearClientSession,
  installAuthInterceptors,
  startCrossTabSync
} from '@/services/authSession'

export default {
  name: 'App',
  components: {
    ErrorNotification: () => import('@/components/global/ErrorNotification.vue'),
    SuccessNotification: () => import('@/components/global/SuccessNotification.vue'),
    Settings: () => import('@/components/global/Settings.vue'),
    EmployeeDetails: () => import('@/components/employee/EmployeeDetails.vue'),
    ProjectDetail: () => import('@/components/projects/ProjectDetails.vue'),
    ClientDetail: () => import('@/components/clients/ClientDetails.vue')
  },
  data () {
    return {
      showErrorNotif: true,
      reloadTimeout: null,
      refreshTokenInterval: null,
      tokenUpdateInterval: 10
    }
  },
  computed: {
    blockedProject () { return this.$store.getters.getBlockedProject },
    employeeDetails () { return this.$store.getters.getEmployeeDetails },
    projectDetails: {
      get () { return this.$store.getters.getProjectDetails },
      set (val) {
        if (!val.show) {
          this.$store.dispatch('projectDetailsScreen', { show: false, project: null })
        }
      }
    },
    clientDetails: {
      get () { return this.$store.getters.getClientDetails },
      set (val) {
        if (!val.show) {
          this.$store.dispatch('clientDetailsScreen', { show: false, client: null })
        }
      }
    },
    user () { return this.$store.getters.getUser },
    overlay: {
      get () { return this.$store.getters.getOverlay },
      set (val) { this.$store.dispatch('showOverlay', { show: val }) }
    }
  },
  destroyed () {
    if (this.refreshTokenInterval) clearInterval(this.refreshTokenInterval)
  },
  beforeCreate () {
    if (!localStorage.getItem('dcmweb')) {
      localStorage.setItem('dcmweb', JSON.stringify({}))
    }
  },
  async mounted () {
    const savedTheme = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')

    if (savedTheme === 'dark') {
      this.$vuetify.theme.dark = true
    } else if (savedTheme === 'light') {
      this.$vuetify.theme.dark = false
    } else {
      this.$vuetify.theme.dark = prefersDark.matches
      prefersDark.onchange = (e) => {
        this.$vuetify.theme.dark = e.matches
      }
    }

    if (this.$route.name === 'ConsiderRequestFromEmail') return
    if (this.$route.name !== 'Login') this.$router.replace({ name: 'Login' })
    this.overlay = false

    installAuthInterceptors(this.axios, this.$store, this.$router, {
      onUnauthorizedUi: () => {
        this.showErrorNotif = false
        setTimeout(() => {
          this.$store.dispatch('setError', { errorMsg: '' })
          this.showErrorNotif = true
        }, 4000)
      }
    })
    startCrossTabSync(this.$store, this.$router)

    this.axios.interceptors.request.use((config) => {
      if ([`/api/employee_role/${this.user.login}`, '/api/notifications', '/api/refreshtoken', '/api/assistant/chat'].indexOf(config.url) === -1) {
        if (config.method !== 'get' && !this.overlay) this.overlay = true
      }
      return config
    }, (error) => {
      this.overlay = false
      return Promise.reject(error)
    })

    this.axios.interceptors.response.use((response) => {
      if (this.overlay) this.overlay = false
      return response
    }, (err) => {
      this.overlay = false
      return Promise.reject(err)
    })

    const lData = readStoredUser()
    if (lData.token) {
      this.$store.dispatch('setUserData', { userData: lData })
      await this.refreshToken()
      const routeName = localStorage.getItem('dcmweb_lpn') || 'Reporting'
      setTimeout(() => { this.$router.replace({ name: routeName }) })
    }
  },
  methods: {
    getRoles () {
      this.axios.get('/api/employee_role/' + this.user.login, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(result => {
        this.$store.dispatch('setEmployeeRoles', { roles: result.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async refreshToken () {
      try {
        await refreshTokens(this.$store, this.axios)
        this.getRoles()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        // Only clear session on 401 (invalid/revoked refresh). Keep tokens on 5xx so retry can succeed.
        if (error.isAuthFailure) {
          if (this.refreshTokenInterval) clearInterval(this.refreshTokenInterval)
          clearClientSession(this.$store, this.$router)
        }
      }
    },
    onCloseEmployeeDetails () {
      this.$store.dispatch('employeeDetailsScreen', {
        show: false,
        employee: null,
        edit: false
      })
    }
  },
  watch: {
    user (newVal) {
      if (this.refreshTokenInterval) {
        clearInterval(this.refreshTokenInterval)
        this.refreshTokenInterval = null
      }
      if (newVal && newVal.token) {
        this.refreshTokenInterval = setInterval(async () => {
          await this.refreshToken()
        }, 1000 * 60 * this.tokenUpdateInterval)
      }
    },
    blockedProject (newVal) {
      if (newVal) {
        if (!this.reloadTimeout) {
          this.reloadTimeout = setTimeout(() => window.location.reload(), 1000 * 60 * 5)
        }
      } else if (this.reloadTimeout) {
        clearTimeout(this.reloadTimeout)
      }
    }
  }
}
</script>

<style lang="scss">
#app {
  background-color: var(--v-background-base) !important;
  transition: background-color 0.4s ease, color 0.4s ease;

  .dialog-content, .v-menu__content {
    background-color: var(--v-surface-base) !important;
    transition: background-color 0.4s ease, color 0.4s ease;
  }
}

.theme-toggle {
  position: fixed;
  top: 870px;
  right: 10px;
  z-index: 2000;
}

.fade-theme-enter-active,
.fade-theme-leave-active {
  transition: opacity 0.4s ease;
}
.fade-theme-enter,
.fade-theme-leave-to {
  opacity: 0;
}
</style>
